import React, { useEffect, useState } from 'react';
import { FiCheck, FiFolder, FiX } from 'react-icons/fi';
import { leaveProject, listConversations } from '../utils/conversationApi';
import './LeaveProjectModal.css';

/**
 * "Leave project" for someone who was invited to it (the creator can't leave
 * their own project -- they delete it).
 *
 * Leaving always keeps the member's OWN conversations from the project (nobody
 * else's are ever touched): they are filed in a "Project-<name>" folder among
 * the member's regular conversations, to look back at their own work. There is
 * deliberately no option to delete them here. From then on they are plain
 * chats, stamped with the project they came from: if the owner invites the
 * member again, the invitation prompt offers to bring them back into the
 * project (or leave them where they are -- the project starts empty then).
 *
 * The member loses access immediately and drops off the owner's members list;
 * getting back in means a fresh invitation.
 *
 * `isLastMember`: the owner has already left and nobody else is in it, so
 * leaving deletes the project for good -- said up front, not after.
 *
 * `onLeft` runs once the member has dismissed the final "you left" screen --
 * not before, since it navigates away from the project and would unmount this.
 */
export default function LeaveProjectModal({
  isOpen, onClose, projectId, projectName, isLastMember = false, onLeft,
}) {
  const [conversationCount, setConversationCount] = useState(null); // null = not known (yet)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    setBusy(false);
    setError('');
    setResult(null);
    setConversationCount(null);
    let cancelled = false;
    // Only to word the message accurately ("your 3 conversations") -- the
    // server works from its own list either way.
    listConversations()
      .then((all) => {
        if (!cancelled) setConversationCount(all.filter((c) => c.project_id === projectId).length);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen, projectId]);

  const done = Boolean(result);
  const dismiss = () => {
    if (busy) return;
    onClose();
    if (done && onLeft) onLeft(result);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') dismiss(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // dismiss closes over the latest state each render; re-binding per change is intended.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, busy, done, result]);

  if (!isOpen) return null;

  const folderName = `Project-${(projectName || 'Untitled').trim()}`;
  const countLabel = conversationCount === null
    ? 'your conversations'
    : `your ${conversationCount} ${conversationCount === 1 ? 'conversation' : 'conversations'}`;

  const handleLeave = async () => {
    setBusy(true);
    setError('');
    try {
      setResult(await leaveProject(projectId));
    } catch (err) {
      setError(err.message || 'Could not leave this project. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lpm-overlay" onClick={dismiss}>
      <div
        className="lpm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lpm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="lpm-close" onClick={dismiss} disabled={busy} aria-label="Close">
          <FiX size={18} />
        </button>

        {done ? (
          <>
            <div className="lpm-body">
              <span className="lpm-done-icon" aria-hidden="true"><FiCheck size={22} /></span>
              <h2 id="lpm-title" className="lpm-title">You left {projectName}</h2>
              {result.conversations_saved > 0 ? (
                <p className="lpm-text">
                  Your {result.conversations_saved} {result.conversations_saved === 1 ? 'conversation is' : 'conversations are'}{' '}
                  saved in the <strong>{result.folder_name}</strong> folder in your Conversations.
                </p>
              ) : (
                <p className="lpm-text">You had no conversations in this project.</p>
              )}
              <p className="lpm-text lpm-muted">
                {result.project_deleted
                  ? 'You were the last person in it, so the project has been deleted.'
                  : 'Anyone in the project with Edit access can invite you back.'}
              </p>
            </div>
            <div className="lpm-footer">
              <button type="button" className="lpm-btn lpm-btn--primary" onClick={dismiss} autoFocus>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="lpm-body">
              <h2 id="lpm-title" className="lpm-title">Leave this project?</h2>
              <p className="lpm-project-name" title={projectName}>{projectName}</p>
              {isLastMember ? (
                <p className="lpm-text lpm-warning">
                  You&apos;re the last person in this project and its owner has already left, so leaving
                  deletes it permanently, including its knowledge base files and instructions.
                </p>
              ) : (
                <p className="lpm-text">
                  You&apos;ll lose access to its knowledge base and instructions, and you won&apos;t be in
                  the members list any more. To come back, someone with Edit access would have to
                  invite you again.
                </p>
              )}

              {conversationCount !== 0 ? (
                <div className="lpm-saved-note">
                  <FiFolder size={16} aria-hidden="true" />
                  <span>
                    {`${countLabel.charAt(0).toUpperCase()}${countLabel.slice(1)}`} in this project will be saved in a
                    folder called <strong>{folderName}</strong> in your regular Conversations, so you can still
                    look back at your work. They stay private to you. If you&apos;re invited back, you can choose
                    to bring them back into the project.
                  </span>
                </div>
              ) : (
                <p className="lpm-text lpm-muted">You have no conversations in this project.</p>
              )}

              {error && <p className="lpm-error" role="alert">{error}</p>}
            </div>

            <div className="lpm-footer">
              <button type="button" className="lpm-btn lpm-btn--ghost" onClick={dismiss} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="lpm-btn lpm-btn--primary" onClick={handleLeave} disabled={busy} autoFocus>
                {busy ? 'Leaving...' : (isLastMember ? 'Leave and delete' : 'Leave project')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
